import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { COLORS, SHADOWS, FONTS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import {
  fetchUserSessions, logoutUser, sendPasswordResetEmail, resetUserPassword,
  type KeycloakUser,
} from '../api/keycloak-admin';

/** Staged changes committed by the parent on save. */
export interface EditUserChanges {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  enabled: boolean;
  makeAdmin: boolean;
}

interface EditUserModalProps {
  user: KeycloakUser;
  isAdmin: boolean;
  /** The acting operator is editing their own account. */
  isSelf: boolean;
  /** This user is the only Vertex admin in the realm. */
  isLastAdmin: boolean;
  isDark: boolean;
  colors: ColorTheme;
  isSaving: boolean;
  onSave: (changes: EditUserChanges) => void;
  onDelete: () => void;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Relative age like "4 days ago" from an epoch-ms timestamp. */
function relativeAge(ms: number): string {
  const secs = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function EditUserModal({
  user, isAdmin, isSelf, isLastAdmin, isDark, colors, isSaving, onSave, onDelete, onClose,
}: EditUserModalProps): React.JSX.Element {
  const { token } = useAuth();

  const [fullName, setFullName] = useState(`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim());
  const [email, setEmail] = useState(user.email ?? '');
  const [username, setUsername] = useState(user.username);
  const [enabled, setEnabled] = useState(user.enabled);
  const [makeAdmin, setMakeAdmin] = useState(isAdmin);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [adminGuard, setAdminGuard] = useState<string | null>(null);

  const [sessions, setSessions] = useState<{ count: number; oldest: number | null } | null>(null);
  const [resetMsg, setResetMsg] = useState<string | null>(null);
  const [logoutMsg, setLogoutMsg] = useState<string | null>(null);

  const [showManual, setShowManual] = useState(false);
  const [manualPw, setManualPw] = useState('');
  const [manualTemp, setManualTemp] = useState(true);
  const [manualVisible, setManualVisible] = useState(false);
  const [manualMsg, setManualMsg] = useState<string | null>(null);

  const fullNameLabel = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.username;

  useEffect(() => {
    let active = true;
    fetchUserSessions(token, user.id)
      .then(list => {
        if (!active) return;
        const starts = list.map(s => s.start).filter(n => typeof n === 'number');
        setSessions({ count: list.length, oldest: starts.length ? Math.min(...starts) : null });
      })
      .catch(() => { if (active) setSessions({ count: 0, oldest: null }); });
    return () => { active = false; };
  }, [token, user.id]);

  function handleAdminToggle(): void {
    const turningOff = makeAdmin;
    if (turningOff && isSelf) {
      setAdminGuard('You cannot remove your own Vertex admin access.');
      return;
    }
    if (turningOff && isLastAdmin) {
      setAdminGuard('This is the last Vertex admin. Assign another admin first.');
      return;
    }
    setAdminGuard(null);
    setMakeAdmin(v => !v);
  }

  async function handleSendReset(): Promise<void> {
    setResetMsg(null);
    try {
      await sendPasswordResetEmail(token, user.id);
      setResetMsg('Reset link sent.');
    } catch (err) {
      setResetMsg(err instanceof Error ? err.message : 'Could not send reset link.');
    }
  }

  async function handleForceLogout(): Promise<void> {
    setLogoutMsg(null);
    try {
      await logoutUser(token, user.id);
      setSessions({ count: 0, oldest: null });
      setLogoutMsg('All sessions ended.');
    } catch (err) {
      setLogoutMsg(err instanceof Error ? err.message : 'Could not end sessions.');
    }
  }

  async function handleManualSet(): Promise<void> {
    setManualMsg(null);
    if (manualPw.length < 6) { setManualMsg('Password must be at least 6 characters.'); return; }
    try {
      await resetUserPassword(token, user.id, manualPw, manualTemp);
      setManualMsg('Password set.');
      setManualPw('');
    } catch (err) {
      setManualMsg(err instanceof Error ? err.message : 'Could not set password.');
    }
  }

  function handleSave(): void {
    if (!EMAIL_RE.test(email.trim())) { setEmailError('Enter a valid email address'); return; }
    const parts = fullName.trim().split(/\s+/);
    onSave({
      firstName: parts[0] ?? '',
      lastName: parts.slice(1).join(' '),
      email: email.trim(),
      username: username.trim(),
      enabled,
      makeAdmin,
    });
  }

  const sessionHint = !sessions
    ? 'Checking sessions...'
    : sessions.count === 0
      ? 'No active sessions.'
      : `${sessions.count} session${sessions.count === 1 ? '' : 's'}${sessions.oldest ? `, oldest ${relativeAge(sessions.oldest)}` : ''}.`;

  const inputStyle: React.CSSProperties = {
    height: '38px', padding: '0 12px', borderRadius: '6px',
    border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt,
    color: colors.text, fontFamily: FONTS.body, fontSize: '13px', outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };
  const labelStyle: React.CSSProperties = {
    fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: colors.textSecondary, marginBottom: '4px', display: 'block',
  };
  const hintStyle: React.CSSProperties = { fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginTop: '6px' };
  const smallBtn: React.CSSProperties = {
    height: '32px', padding: '0 12px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent', color: colors.text, fontFamily: FONTS.body, fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap',
  };
  const secondaryBtn: React.CSSProperties = {
    height: '34px', padding: '0 14px', borderRadius: '6px', border: `1px solid ${colors.border}`,
    backgroundColor: 'transparent', color: colors.text, fontFamily: FONTS.heading, fontSize: '11px',
    letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
  };
  const primaryBtn: React.CSSProperties = {
    height: '34px', padding: '0 16px', borderRadius: '6px', border: 'none',
    background: isDark ? 'linear-gradient(90deg, #0266C6, #00407D)' : 'linear-gradient(90deg, #01A9ED, #0165C6)',
    color: '#FFFFFF', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.12em',
    textTransform: 'uppercase', cursor: isSaving ? 'not-allowed' : 'pointer', opacity: isSaving ? 0.6 : 1,
  };

  function Toggle({ on, onToggle, label }: { on: boolean; onToggle: () => void; label: string }): React.JSX.Element {
    return (
      <span
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={onToggle}
        style={{
          position: 'relative', width: '36px', height: '20px', flexShrink: 0, borderRadius: '999px', cursor: 'pointer',
          backgroundColor: on ? COLORS.accent.blue : (isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt),
          border: `1px solid ${on ? COLORS.accent.blue : colors.border}`, transition: 'background-color 0.15s',
        }}
      >
        <span style={{
          position: 'absolute', top: '2px', left: on ? '17px' : '2px', width: '14px', height: '14px',
          borderRadius: '50%', backgroundColor: on ? '#FFFFFF' : colors.textSecondary, transition: 'left 0.15s',
        }} />
      </span>
    );
  }

  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', padding: '10px 0' };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 50 }} />

      <div className="vx-modal-fit" style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: '480px', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
        backgroundColor: colors.card, borderRadius: '12px',
        boxShadow: isDark ? SHADOWS.neumorphicDark : SHADOWS.neumorphicLight, zIndex: 51,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', padding: '24px 24px 16px' }}>
          <div>
            <h2 style={{ fontFamily: FONTS.heading, fontSize: '14px', letterSpacing: '0.2em', textTransform: 'uppercase', color: colors.text, margin: '0 0 4px' }}>
              Edit user
            </h2>
            <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.textSecondary }}>{fullNameLabel}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: colors.textSecondary }} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div style={{ padding: '0 24px', overflowY: 'auto', flex: 1 }}>
          {/* Identity fields */}
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Full name</label>
            <input value={fullName} onChange={e => setFullName(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={labelStyle}>Email</label>
            <input
              type="email" value={email}
              onChange={e => { setEmail(e.target.value); setEmailError(null); }}
              onBlur={() => setEmailError(email.trim() && !EMAIL_RE.test(email.trim()) ? 'Enter a valid email address' : null)}
              style={{ ...inputStyle, borderColor: emailError ? '#EF4444' : colors.border }}
            />
            {emailError && <div style={{ ...hintStyle, color: '#EF4444' }}>{emailError}</div>}
          </div>
          <div style={{ marginBottom: '4px' }}>
            <label style={labelStyle}>Username</label>
            <input value={username} onChange={e => setUsername(e.target.value)} style={{ ...inputStyle, fontFamily: FONTS.mono }} />
            <div style={hintStyle}>Changing this changes how the user signs in.</div>
          </div>

          <div style={{ borderTop: `1px solid ${colors.border}`, margin: '18px 0 14px' }} />
          <div style={{ ...labelStyle, marginBottom: '8px' }}>Account</div>

          {/* Password */}
          <div style={rowStyle}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Password</div>
              <div style={hintStyle}>Sends a reset link to the user.</div>
            </div>
            <button onClick={handleSendReset} style={smallBtn}>Send reset</button>
          </div>
          <div style={{ marginTop: '-2px', marginBottom: '4px' }}>
            {resetMsg && <div style={hintStyle}>{resetMsg}</div>}
            {!showManual ? (
              <button onClick={() => setShowManual(true)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: COLORS.accent.blue, fontFamily: FONTS.body, fontSize: '11px' }}>
                Set manually
              </button>
            ) : (
              <div style={{ marginTop: '8px', padding: '12px', borderRadius: '8px', border: `1px solid ${colors.border}`, backgroundColor: isDark ? COLORS.dark.cardAlt : COLORS.light.cardAlt }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type={manualVisible ? 'text' : 'password'} autoComplete="new-password" placeholder="New password"
                    value={manualPw} onChange={e => setManualPw(e.target.value)}
                    style={{ ...inputStyle, paddingRight: '40px', backgroundColor: colors.card }}
                  />
                  <button type="button" onClick={() => setManualVisible(v => !v)} aria-label={manualVisible ? 'Hide password' : 'Show password'}
                    style={{ position: 'absolute', right: '6px', top: 0, height: '100%', display: 'flex', alignItems: 'center', padding: '0 6px', background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
                    {manualVisible
                      ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>}
                  </button>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={manualTemp} onChange={e => setManualTemp(e.target.checked)} style={{ accentColor: COLORS.accent.blue }} />
                  <span style={{ fontFamily: FONTS.body, fontSize: '12px', color: colors.textSecondary }}>Require change at next sign-in</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button onClick={handleManualSet} style={smallBtn}>Set password</button>
                  <button onClick={() => { setShowManual(false); setManualPw(''); setManualMsg(null); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: colors.textSecondary, fontFamily: FONTS.body, fontSize: '11px' }}>Cancel</button>
                  {manualMsg && <span style={{ ...hintStyle, marginTop: 0 }}>{manualMsg}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Active sessions */}
          <div style={rowStyle}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Active sessions</div>
              <div style={hintStyle}>{logoutMsg ?? sessionHint}</div>
            </div>
            <button onClick={handleForceLogout} style={smallBtn} disabled={sessions?.count === 0}>Force logout</button>
          </div>

          {/* Vertex admin */}
          <div style={rowStyle}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Vertex admin</div>
              <div style={hintStyle}>Full access to Vertex, including all users.</div>
            </div>
            <Toggle on={makeAdmin} onToggle={handleAdminToggle} label="Vertex admin" />
          </div>
          {adminGuard && (
            <div style={{ ...hintStyle, color: COLORS.accent.gold, marginTop: 0, marginBottom: '4px' }}>{adminGuard}</div>
          )}
          {makeAdmin && (
            <div style={{ display: 'flex', gap: '10px', padding: '11px 13px', margin: '4px 0 8px', borderRadius: '8px', backgroundColor: isDark ? 'rgba(244, 196, 63, 0.12)' : 'rgba(244, 196, 63, 0.08)', border: '1px solid rgba(244, 196, 63, 0.3)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <p style={{ margin: 0, fontFamily: FONTS.body, fontSize: '12px', color: COLORS.accent.gold, lineHeight: 1.5 }}>
                Granting admin lets this user create, edit, and delete every account in Vertex.
              </p>
            </div>
          )}

          {/* Account enabled */}
          <div style={{ ...rowStyle, marginBottom: '8px' }}>
            <div>
              <div style={{ fontFamily: FONTS.body, fontSize: '13px', color: colors.text }}>Account enabled</div>
              <div style={hintStyle}>Disabled users cannot sign in.</div>
            </div>
            <Toggle on={enabled} onToggle={() => setEnabled(v => !v)} label="Account enabled" />
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', padding: '16px 24px', borderTop: `1px solid ${colors.border}` }}>
          <button onClick={onDelete} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#EF4444', fontFamily: FONTS.heading, fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Delete user
          </button>
          <div style={{ display: 'flex', gap: '9px' }}>
            <button onClick={onClose} disabled={isSaving} style={secondaryBtn}>Cancel</button>
            <button onClick={handleSave} disabled={isSaving} style={primaryBtn}>{isSaving ? 'Saving...' : 'Save changes'}</button>
          </div>
        </div>
      </div>
    </>
  );
}
